import { differenceInDays, format, parseISO, subDays } from 'date-fns';
import {
  Dispatch,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  NotificationDefault,
  NotificationListOrderBy,
  usePushNotificationListQuery,
  usePushNotificationTotalQuery,
} from '../../../../../services';
import { getWebAppUrl, OrderDirection, SearchField, useDebounce } from '../../../../../shared';
import {
  ChartWrapper,
  DataRowItem,
  Loader,
  TableChart,
  TableChartHeader,
  TableStats,
} from '../../helpers';
import { ContentProps } from '../Content';

import classes from '../../Analytics.module.scss';

interface PushLinkArgs {
  notification: NotificationDefault;
  webappUrl: string;
}

export const PushNotificationList: FunctionComponent<ContentProps> = memo(
  ({ from, to, channelId, platform }) => {
    const { t } = useTranslation();

    const [orderBy, setOrderBy] = useState<NotificationListOrderBy>(
      NotificationListOrderBy.DELIVERY_TIME
    );

    const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC);

    const [page, setPage] = useState<number>(1);

    const [query, setQuery] = useState<string>('');

    const debouncedQuery = useDebounce<string>(query);

    const { data: pushNotificationList, isFetching: isFetchingPushNotificationList } =
      usePushNotificationListQuery({
        from,
        to,
        channelId,
        platform,
        orderBy,
        orderDirection,
        page,
        query: debouncedQuery,
      });

    const { data: pushNotificationTotal, isFetching: isFetchingPushNotificationTotal } =
      usePushNotificationTotalQuery({
        from,
        to,
        channelId,
        platform,
      });

    const daysDiff = differenceInDays(new Date(to), new Date(from)) + 1;

    const { data: pushNotificationTotalPrev, isFetching: isFetchingPushNotificationTotalPrev } =
      usePushNotificationTotalQuery({
        from: subDays(new Date(from), daysDiff).toISOString(),
        to: subDays(new Date(to), daysDiff).toISOString(),
        channelId,
        platform,
      });

    const renderStats = useMemo(() => {
      if (!pushNotificationTotal || !pushNotificationTotalPrev) {
        return null;
      }

      const { items } = pushNotificationTotal;

      const { items: itemsPrev } = pushNotificationTotalPrev;

      const stats = [
        {
          localiseKey: 'notificationListLabels.total',
          hintLocaliseKey: 'pushNotificationListHints.total',
          value: items.find(({ label }) => label === 'total')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'total')?.value ?? 0,
        },
        {
          localiseKey: 'notificationListLabels.total-open',
          hintLocaliseKey: 'pushNotificationListHints.total-open',
          value: items.find(({ label }) => label === 'openTotal')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'openTotal')?.value ?? 0,
        },
        {
          localiseKey: 'notificationListLabels.average-sent',
          hintLocaliseKey: 'pushNotificationListHints.average-sent',
          value: items.find(({ label }) => label === 'sentAvg')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'sentAvg')?.value ?? 0,
        },
        {
          localiseKey: 'notificationListLabels.average-open',
          hintLocaliseKey: 'pushNotificationListHints.average-open',
          value: items.find(({ label }) => label === 'openAvg')?.value ?? 0,
          valuePrev: itemsPrev.find(({ label }) => label === 'openAvg')?.value ?? 0,
        },
      ];

      return (
        <TableStats
          stats={stats}
          loading={Boolean(isFetchingPushNotificationTotal || isFetchingPushNotificationTotalPrev)}
        />
      );
    }, [
      isFetchingPushNotificationTotal,
      isFetchingPushNotificationTotalPrev,
      pushNotificationTotal,
      pushNotificationTotalPrev,
    ]);

    const getPushLink = useCallback(
      ({ notification, webappUrl }: PushLinkArgs): DataRowItem => {
        const { url, storyCard } = notification;

        const { id: cardId = 0, storyId = 0 } = { ...storyCard };

        const notificationUrl = url ?? '';

        const attachmentUrl = `${webappUrl}/admin/details/${storyId}/${cardId}`;

        const linkValue = storyCard ? t('common.attachment') : notificationUrl;

        const linkUrl = storyCard ? attachmentUrl : notificationUrl;

        return { value: linkValue, url: linkUrl };
      },
      [t]
    );

    const renderChart = useMemo(() => {
      if (!pushNotificationList) {
        return <Loader />;
      }

      const { items, pageInfo } = pushNotificationList;

      const data: DataRowItem[][] = items.map(({ label, payload }) => {
        const { sent, open, channel } = payload;

        const { name: channelName = '', subdomain = '' } = { ...channel };

        const { notification } = payload;

        const link = getPushLink({ notification, webappUrl: getWebAppUrl(subdomain) });

        return [
          { value: label },
          { value: sent },
          { value: open },
          { value: format(parseISO(notification.deliveredTime), 'dd.MM.yy, HH:mm') },
          { ...link },
          { value: channelName },
        ];
      });

      const headers: TableChartHeader[] = [
        { label: t('notificationListLabels.title') },
        { label: t('notificationListLabels.sent'), orderBy: NotificationListOrderBy.SENT },
        { label: t('notificationListLabels.open'), orderBy: NotificationListOrderBy.OPEN },
        {
          label: t('notificationListLabels.delivery-time'),
          orderBy: NotificationListOrderBy.DELIVERY_TIME,
        },
        { label: t('notificationListLabels.link') },
        { label: t('notificationListLabels.channel') },
      ];

      return (
        <TableChart
          sticky
          headers={headers}
          data={data}
          pageInfo={pageInfo}
          setPage={setPage}
          orderBy={orderBy}
          setOrderBy={setOrderBy as Dispatch<SetStateAction<string>>}
          orderDirection={orderDirection}
          setOrderDirection={setOrderDirection}
          loading={isFetchingPushNotificationList}
        />
      );
    }, [
      getPushLink,
      isFetchingPushNotificationList,
      orderBy,
      orderDirection,
      pushNotificationList,
      t,
    ]);

    return (
      <ChartWrapper
        typeLocaliseKey={'chartTypes.push-notification-list'}
        infoLocaliseKey={'pushNotificationList.info'}
        component={
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={t('common.type-to-search')}
            className={classes['analytics__chart-header-search']}
          />
        }
      >
        {renderStats}
        {renderChart}
      </ChartWrapper>
    );
  }
);
